import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Checkmark,
  LoadingCircle,
  MessageLine,
  EmptyCircle,
  Container,
  MessageWrapper,
} from './styled-components';

const MAX_TREATMENTS_SHOWN = 2;

const getLoadingMessages = (
  locality,
  insuranceProvider,
  selectedTreatments,
) => {
  const selectedTreatmentMessages = selectedTreatments
    ?.slice(0, MAX_TREATMENTS_SHOWN)
    .map((treatment, index) => ({
      name: `treatment-${index}`,
      text: `Filtering dentists who offer ${treatment}...`,
    }));

  return [
    {
      name: 'locality',
      text: locality && `Finding dentists near ${locality}...`,
    },
    {
      name: 'insurance-provider',
      text:
        insuranceProvider
        && `Filtering dentists that accept ${insuranceProvider}...`,
    },
    ...selectedTreatmentMessages || [],
    {
      name: 'default',
      text: 'Finding dentists who meet your preferences',
    },
  ].filter(({ text }) => text);
};

const getMessageIcon = (
  numMessages,
  indexToGetIconFor,
  indexOfLoadingMessage,
) => {
  // Show the last message as loading forever
  const isLastMessage =
    indexOfLoadingMessage >= numMessages && indexToGetIconFor === numMessages;
  if (isLastMessage || indexToGetIconFor === indexOfLoadingMessage) {
    return (
      <LoadingCircle data-testid={`loading-circle-${indexToGetIconFor}`} />
    );
  }

  if (indexToGetIconFor < indexOfLoadingMessage) {
    return <Checkmark data-testid={`checkmark-${indexToGetIconFor}`} />;
  }

  return <EmptyCircle data-testid={`empty-circle-${indexToGetIconFor}`} />;
};

export const LoadingMatch = ({
  minimumLoadDuration,
  locality,
  insuranceProvider,
  selectedTreatments,
}) => {
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  const loadingMessages = getLoadingMessages(
    locality,
    insuranceProvider,
    selectedTreatments,
  );

  useEffect(() => {
    const numMessages = loadingMessages.length;
    const timePerMessage = minimumLoadDuration / (numMessages + 1);

    const interval = setTimeout(() => {
      setLoadingMessageIndex(loadingMessageIndex + 1);
    }, timePerMessage);

    return () => clearTimeout(interval);
  }, [loadingMessageIndex]);

  return (
    <Container data-testid="searching-title" tabIndex={0}>
      <MessageWrapper data-testid="loading-message-wrapper">
        {loadingMessages.map((message, index) => (
          <MessageLine
            key={message.name}
            data-testid={`loading-message-${message.name}`}
          >
            {getMessageIcon(
              loadingMessages.length - 1,
              index,
              loadingMessageIndex,
            )}
            {message.text}
          </MessageLine>
        ))}
      </MessageWrapper>
    </Container>
  );
};

LoadingMatch.propTypes = {
  locality: PropTypes.string,
  insuranceProvider: PropTypes.string,
  selectedTreatments: PropTypes.arrayOf(PropTypes.string),

  minimumLoadDuration: PropTypes.number.isRequired,
};
